import { createFileRoute } from "@tanstack/react-router";
import { loadAuthorIndex } from "~/utils/content-fns";
import { seo, seoLinks, breadcrumbJsonLd } from "~/utils/seo";
import { Breadcrumbs } from "~/components/Breadcrumbs";
import { AuthorAvatar } from "~/components/AuthorAvatar";

const CRUMBS = [{ name: "Home", url: "/" }, { name: "Authors" }];

const TITLE = "C1.ai authors";
const DESCRIPTION =
  "The people behind the C1.ai writing on identity security, access governance, and AI agents — security leaders, engineers, and product teams.";

export const Route = createFileRoute("/authors/")({
  loader: () => loadAuthorIndex(),
  head: () => ({
    meta: seo({ title: TITLE, description: DESCRIPTION, url: "/authors" }),
    links: seoLinks({ url: "/authors" }),
    scripts: [breadcrumbJsonLd(CRUMBS)].filter((s) => s !== null),
  }),
  component: AuthorsIndex,
});

function AuthorsIndex() {
  const authors = Route.useLoaderData();

  return (
    <div className="px-6 md:px-8">
      <div className="mx-auto max-w-screen-xl py-10 md:py-14">
        <Breadcrumbs items={CRUMBS} />

        <h1 className="mt-6 font-display text-4xl font-bold text-charcoal md:text-5xl">
          {TITLE}
        </h1>
        <p className="mt-3 max-w-[46rem] text-lg leading-relaxed text-gray-80">
          {DESCRIPTION}
        </p>

        <ul className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {authors.map((a) => (
            <li
              key={a.key}
              className="group relative rounded-xl border border-[#DCDADA] bg-white p-6"
            >
              <div className="flex items-center gap-4">
                <AuthorAvatar
                  name={a.name}
                  image={a.image}
                  className="h-14 w-14 shrink-0 text-base"
                />
                <div className="min-w-0">
                  <h2 className="font-body text-base font-bold leading-snug text-charcoal">
                    {/* Title carries the link text; after:absolute makes the
                        whole card a target without emptying the anchor. */}
                    <a
                      href={`/authors/${a.key}`}
                      className="after:absolute after:inset-0 after:content-[''] group-hover:text-teal-dark"
                    >
                      {a.name}
                    </a>
                  </h2>
                  {a.title && (
                    <p className="mt-0.5 text-sm leading-snug text-gray-500">{a.title}</p>
                  )}
                </div>
              </div>
              {a.bio && (
                <p className="mt-4 line-clamp-3 text-sm leading-relaxed text-gray-80">
                  {a.bio}
                </p>
              )}
              <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-gray-500">
                {a.count === 1 ? "1 article" : `${a.count} articles`}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
